import { Radii, Size } from '~/types/ThemeTypes'
import { createMultiStyleConfigHelpers } from '~/utils/styleHelpers'

import { asChakraVariable } from '@mangovoice/design-system'
import { FontWeight } from '@mangovoice/design-system'
import { textStyleKeys } from '@mangovoice/design-system'

const mangoButtonParts = ['button', 'text', 'nodeContainer', 'icon'] as const

const helpers = createMultiStyleConfigHelpers(mangoButtonParts)

const shadowVar = (token: 'default' | 'focus' | 'error' | 'success') =>
  `var(--chakra-shadows-${token})`

const clearStateShadow = () => ({ boxShadow: 'none' })

export enum MangoButtonVariant {
  Default_100 = 'default_100',
  Default_200 = 'default_200',
  Default_300 = 'default_300',
  Destructive_100 = 'destructive_100',
  Destructive_200 = 'destructive_200',
  Destructive_300 = 'destructive_300',
  Success_100 = 'success_100',
  Success_200 = 'success_200',
  Success_300 = 'success_300',
  Brand = 'brand',
  Brand_Text = 'brand_text',
  Neutral_100 = 'neutral_100',
  Neutral_200 = 'neutral_200',
  Neutral_300 = 'neutral_300',
  Login = 'login',
}

export const MangoButtonStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    button: {
      display: 'flex',
      gap: '0.5rem',
      flexShrink: 0,
      borderRadius: Radii.sm,
      border: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
      // Clears the shared Button recipe's default hover shadow.
      _hover: clearStateShadow(),
      _active: clearStateShadow(),
      _expanded: clearStateShadow(),
      _disabled: { cursor: 'not-allowed', opacity: 1, userSelect: 'none' },
    },
    nodeContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    icon: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    text: { textStyle: textStyleKeys.Body2, margin: 0 },
  },
  sizes: {
    [Size.md]: {
      button: { px: '1.25rem', height: '2.5rem', minHeight: '2.5rem' },
      icon: { boxSize: '1.25rem' },
    },
    [Size.sm]: {
      button: { px: '0.75rem', height: '1.75rem', minHeight: '1.75rem' },
      icon: { boxSize: '1rem' },
    },
  },
  variants: {
    [MangoButtonVariant.Brand]: {
      button: {
        backgroundColor: asChakraVariable('brand.solid'),
        _hover: {
          backgroundColor: asChakraVariable('brand.emphasized'),
          boxShadow: shadowVar('default'),
        },
        _active: {
          backgroundColor: asChakraVariable('brand.emphasized'),
          boxShadow: shadowVar('default'),
        },
        _disabled: {
          backgroundColor: asChakraVariable('bg.muted'),
          _hover: {
            backgroundColor: asChakraVariable('bg.muted'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.muted'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('brand.contrast'),
        _groupHover: { color: asChakraVariable('brand.contrast') },
        _groupActive: { color: asChakraVariable('brand.contrast') },
        _groupDisabled: { color: asChakraVariable('brand.contrast') },
      },
      icon: {
        color: asChakraVariable('brand.contrast'),
        _groupHover: { color: asChakraVariable('brand.contrast') },
        _groupActive: { color: asChakraVariable('brand.contrast') },
        _groupDisabled: { color: asChakraVariable('brand.contrast') },
      },
    },
    [MangoButtonVariant.Brand_Text]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: 'transparent' },
        _active: { backgroundColor: 'transparent' },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: { backgroundColor: 'transparent' },
          _active: { backgroundColor: 'transparent' },
        },
      },
      text: {
        color: asChakraVariable('brand.fg'),
        _groupHover: { color: asChakraVariable('brand.fg') },
        _groupActive: { color: asChakraVariable('brand.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('brand.fg'),
        _groupHover: { color: asChakraVariable('brand.fg') },
        _groupActive: { color: asChakraVariable('brand.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Default_300]: {
      button: {
        backgroundColor: asChakraVariable('brand.solid'),
        _hover: {
          backgroundColor: asChakraVariable('brand.emphasized'),
          boxShadow: shadowVar('focus'),
        },
        _active: {
          backgroundColor: asChakraVariable('brand.emphasized'),
          boxShadow: shadowVar('focus'),
        },
        _disabled: {
          backgroundColor: asChakraVariable('bg.emphasized'),
          _hover: {
            backgroundColor: asChakraVariable('bg.emphasized'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.emphasized'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: asChakraVariable('brand.contrast'),
        _groupHover: { color: asChakraVariable('brand.contrast') },
        _groupActive: { color: asChakraVariable('brand.contrast') },
        _groupDisabled: { color: asChakraVariable('brand.contrast') },
      },
      icon: {
        color: asChakraVariable('brand.contrast'),
        _groupHover: { color: asChakraVariable('brand.contrast') },
        _groupActive: { color: asChakraVariable('brand.contrast') },
        _groupDisabled: { color: asChakraVariable('brand.contrast') },
      },
    },
    [MangoButtonVariant.Default_200]: {
      button: {
        backgroundColor: asChakraVariable('bg.muted'),
        _hover: { backgroundColor: asChakraVariable('bg.emphasized') },
        _active: { backgroundColor: asChakraVariable('bg.emphasized') },
        _disabled: {
          backgroundColor: asChakraVariable('bg.subtle'),
          _hover: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        fontWeight: FontWeight.Normal,
        fontSize: '14px',
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        fontSize: '14px',
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Default_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: asChakraVariable('bg.muted') },
        _active: { backgroundColor: asChakraVariable('bg.muted') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Destructive_300]: {
      button: {
        backgroundColor: asChakraVariable('red.solid'),
        _hover: {
          backgroundColor: asChakraVariable('red.emphasized'),
          boxShadow: shadowVar('error'),
        },
        _active: {
          backgroundColor: asChakraVariable('red.solid'),
          boxShadow: shadowVar('error'),
        },
        _disabled: {
          backgroundColor: asChakraVariable('bg.emphasized'),
          _hover: {
            backgroundColor: asChakraVariable('bg.emphasized'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.emphasized'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: asChakraVariable('red.contrast'),
        _groupHover: { color: asChakraVariable('red.contrast') },
        _groupActive: { color: asChakraVariable('red.contrast') },
        _groupDisabled: { color: asChakraVariable('red.contrast') },
      },
      icon: {
        color: asChakraVariable('red.contrast'),
        _groupHover: { color: asChakraVariable('red.contrast') },
        _groupActive: { color: asChakraVariable('red.contrast') },
        _groupDisabled: { color: asChakraVariable('red.contrast') },
      },
    },
    [MangoButtonVariant.Destructive_200]: {
      button: {
        backgroundColor: asChakraVariable('red.subtle'),
        _hover: { backgroundColor: asChakraVariable('red.muted') },
        _active: { backgroundColor: asChakraVariable('red.muted') },
        _disabled: {
          backgroundColor: asChakraVariable('bg.subtle'),
          _hover: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('red.fg'),
        _groupHover: { color: asChakraVariable('red.fg') },
        _groupActive: { color: asChakraVariable('red.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('red.fg'),
        _groupHover: { color: asChakraVariable('red.fg') },
        _groupActive: { color: asChakraVariable('red.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Destructive_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: asChakraVariable('red.muted') },
        _active: { backgroundColor: asChakraVariable('red.muted') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('red.fg'),
        _groupHover: { color: asChakraVariable('red.fg') },
        _groupActive: { color: asChakraVariable('red.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('red.fg'),
        _groupHover: { color: asChakraVariable('red.fg') },
        _groupActive: { color: asChakraVariable('red.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Success_300]: {
      button: {
        backgroundColor: asChakraVariable('green.solid'),
        _hover: {
          backgroundColor: asChakraVariable('green.emphasized'),
          boxShadow: shadowVar('success'),
        },
        _active: {
          backgroundColor: asChakraVariable('green.solid'),
          boxShadow: shadowVar('success'),
        },
        _disabled: {
          backgroundColor: asChakraVariable('bg.muted'),
          _hover: {
            backgroundColor: asChakraVariable('bg.muted'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.muted'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: asChakraVariable('green.contrast'),
        _groupHover: { color: asChakraVariable('green.contrast') },
        _groupActive: { color: asChakraVariable('green.contrast') },
        _groupDisabled: { color: asChakraVariable('green.contrast') },
      },
      icon: {
        color: asChakraVariable('green.contrast'),
        _groupHover: { color: asChakraVariable('green.contrast') },
        _groupActive: { color: asChakraVariable('green.contrast') },
        _groupDisabled: { color: asChakraVariable('green.contrast') },
      },
    },
    [MangoButtonVariant.Success_200]: {
      button: {
        backgroundColor: asChakraVariable('bg.subtle'),
        _hover: { backgroundColor: asChakraVariable('green.subtle') },
        _active: { backgroundColor: asChakraVariable('green.muted') },
        _disabled: {
          backgroundColor: asChakraVariable('bg.subtle'),
          _hover: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('green.fg') },
        _groupActive: { color: asChakraVariable('green.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('green.fg') },
        _groupActive: { color: asChakraVariable('green.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Success_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: asChakraVariable('bg.muted') },
        _active: { backgroundColor: asChakraVariable('bg.emphasized') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Neutral_100]: {
      button: {
        backgroundColor: 'transparent',
        _hover: { backgroundColor: asChakraVariable('bg.muted') },
        _active: { backgroundColor: asChakraVariable('bg.emphasized') },
        _disabled: {
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Neutral_200]: {
      button: {
        backgroundColor: asChakraVariable('bg.default'),
        _hover: { backgroundColor: asChakraVariable('bg.emphasized') },
        _active: { backgroundColor: asChakraVariable('bg.muted') },
        _disabled: {
          backgroundColor: asChakraVariable('bg.subtle'),
          _hover: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('fg.default') },
        _groupActive: { color: asChakraVariable('fg.default') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Neutral_300]: {
      button: {
        backgroundColor: asChakraVariable('bg.default'),
        _hover: {
          backgroundColor: asChakraVariable('bg.emphasized'),
          boxShadow: shadowVar('default'),
        },
        _active: {
          backgroundColor: asChakraVariable('bg.emphasized'),
          boxShadow: `0 0.25rem 0.75rem 0 ${asChakraVariable('bg.muted')}`,
        },
        _disabled: {
          backgroundColor: asChakraVariable('bg.muted'),
          _hover: {
            backgroundColor: asChakraVariable('bg.muted'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.muted'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        textStyle: textStyleKeys.Body2_Semibold,
        color: asChakraVariable('fg.muted'),
        _groupHover: { color: asChakraVariable('fg.muted') },
        _groupActive: { color: asChakraVariable('fg.muted') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('fg.muted'),
        _groupHover: { color: asChakraVariable('fg.muted') },
        _groupActive: { color: asChakraVariable('fg.muted') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
    [MangoButtonVariant.Login]: {
      button: {
        backgroundColor: asChakraVariable('bg.subtle'),
        _hover: { backgroundColor: asChakraVariable('brand.subtle') },
        _active: { backgroundColor: asChakraVariable('brand.muted') },
        _disabled: {
          backgroundColor: asChakraVariable('bg.subtle'),
          _hover: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
          _active: {
            backgroundColor: asChakraVariable('bg.subtle'),
            boxShadow: 'none',
          },
        },
      },
      text: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('brand.fg') },
        _groupActive: { color: asChakraVariable('brand.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
      icon: {
        color: asChakraVariable('fg.default'),
        _groupHover: { color: asChakraVariable('brand.fg') },
        _groupActive: { color: asChakraVariable('brand.fg') },
        _groupDisabled: { color: asChakraVariable('fg.muted') },
      },
    },
  },
})
